import { useState, useEffect, useContext } from "react";
import { supabase } from "../supabase";
import { CartContext, AuthContext, CartUIContext, AdminContext, BrandContext } from "../contexts";
import { Btn } from "./ui";
import { useDialogA11y } from "./dialogA11y";
import { getPrimaryImage } from "../utils/helpers";
import { resolveDiscountCode } from "../utils/resolveDiscountCode";
import { priceCart } from "../utils/bulkPricing";

const CartModal = ({ setView }) => {
  const { cart, dispatch, appliedCoupon, setAppliedCoupon } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { cartOpen, setCartOpen } = useContext(CartUIContext);
  const { inventoryManagement } = useContext(AdminContext);
  const b = useContext(BrandContext);
  const freeThreshold = b.freeShippingEnabled === false ? Infinity : (b.freeShippingThreshold ?? 100);
  const pricing = priceCart({ items: cart, coupon: appliedCoupon, freeThreshold, flatShippingRate: b.flatShippingRate ?? 0, freeShippingEnabled: b.freeShippingEnabled });
  const { subtotal, bulkSavings, discountedUnits, eligibleUnits, postBulk, couponDiscount: discount, shipping, totalBeforeCredit: total } = pricing;
  const freeShippingLeft = freeThreshold === Infinity ? 0 : Math.max(0, freeThreshold - postBulk);
  const remainder = eligibleUnits % 10;
  const nudgeCount = remainder >= 7 ? 10 - remainder : 0;
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [affDismissed, setAffDismissed] = useState(false);

  // Auto-apply affiliate discount from ref link (once, on mount)
  useEffect(() => {
    const refCode = localStorage.getItem("_ref");
    if (!refCode || appliedCoupon || affDismissed) return;
    supabase.from("affiliates").select("code, commission_value").eq("code", refCode.toUpperCase()).eq("active", true).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setAppliedCoupon({ code: data.code, type: "percent", value: Number(data.commission_value), min_subtotal: 0, active: true, isAffiliate: true });
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    setCouponError("");

    const result = await resolveDiscountCode(code, postBulk);
    setCouponLoading(false);
    if (result.error) { setCouponError(result.error); return; }
    setAppliedCoupon(result.coupon);
    if (result.coupon.isAffiliate) localStorage.setItem("_ref", result.coupon.code);
    setCouponInput("");
  };

  const { ref: dialogRef, onKeyDown: dialogKeyDown, titleId, dialogProps } = useDialogA11y({
    open: cartOpen,
    onClose: () => setCartOpen(false),
  });

  if (!cartOpen) return null;

  const handleCheckout = () => {
    setCartOpen(false);
    user ? setView("checkout") : setView("auth");
    window.umami?.track("begin-checkout", { items: cart.length });
  };

  return (
    <div
      onClick={() => setCartOpen(false)}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.72)",
        zIndex: 500,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "20px",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
        onKeyDown={dialogKeyDown}
        {...dialogProps}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          width: "100%", maxWidth: 580,
          maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          animation: "fadeUp 0.25s ease",
          boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 id={titleId} style={{ fontSize: "24px" }}>Your Cart</h2>
            {cart.length > 0 && (
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--text-muted)" }}>
                {cart.reduce((s, i) => s + i.qty, 0)} item{cart.reduce((s, i) => s + i.qty, 0) !== 1 ? "s" : ""}
              </span>
            )}
          </div>
          <button
            onClick={() => setCartOpen(false)}
            style={{
              width: 32, height: 32,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-dim)", fontSize: "18px",
              border: "1px solid var(--border)", borderRadius: "var(--radius)",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--amber)"; e.currentTarget.style.color = "var(--text)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text-dim)"; }}
          >✕</button>
        </div>

        {/* Free shipping progress bar */}
        {cart.length > 0 && (
          <div style={{ padding: "12px 24px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
            {freeShippingLeft > 0 ? (
              <>
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", marginBottom: 6 }}>
                  Add <span style={{ color: "var(--amber)" }}>${freeShippingLeft.toFixed(2)}</span> more for free shipping
                </p>
                <div style={{ height: 3, background: "var(--surface2)", borderRadius: 2, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (postBulk / freeThreshold) * 100)}%`, background: "var(--amber)", borderRadius: 2, transition: "width 0.4s ease" }} />
                </div>
              </>
            ) : (
              <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--green)" }}>✓ You've unlocked free shipping!</p>
            )}
          </div>
        )}

        {/* Cart items — scrollable */}
        <div style={{ overflowY: "auto", flex: 1, padding: "0 24px" }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: "center", padding: "52px 0" }}>
              <p style={{ fontSize: "40px", marginBottom: 12 }}>🛒</p>
              <h3 style={{ fontSize: "20px", marginBottom: 6 }}>Your cart is empty</h3>
              <p style={{ color: "var(--text-dim)", fontSize: "14px", marginBottom: 24 }}>Add some items to get started.</p>
              <Btn small onClick={() => setCartOpen(false)}>Browse Shop</Btn>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} style={{
                display: "flex", gap: 14, padding: "16px 0",
                borderBottom: "1px solid var(--border)", alignItems: "center",
              }}>
                <img src={getPrimaryImage(item)} alt={item.name} style={{ width: 68, height: 68, objectFit: "cover", borderRadius: "var(--radius)", border: "1px solid var(--border)", flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: "15px", fontWeight: 700, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</p>
                  <p style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", marginBottom: 8 }}>{item.sku}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => dispatch({ type: "UPDATE_QTY", id: item.id, qty: item.qty - 1 })} style={{ width: 26, height: 26, borderRadius: "var(--radius)", border: "1px solid var(--border)", color: "var(--text)", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={item.qty}
                      onChange={e => {
                        const raw = e.target.value.replace(/\D/g, "");
                        if (raw === "") return;
                        const n = parseInt(raw, 10);
                        const max = inventoryManagement ? item.stock : Infinity;
                        dispatch({ type: "UPDATE_QTY", id: item.id, qty: Math.min(Math.max(n, 1), max) });
                      }}
                      style={{ width: 40, textAlign: "center", fontFamily: "var(--font-mono)", fontSize: "13px", padding: "3px 4px", border: "1px solid var(--border)", borderRadius: "var(--radius)", background: "var(--surface2)", color: "var(--text)" }}
                    />
                    <button
                      onClick={() => { if (inventoryManagement && item.qty >= item.stock) return; dispatch({ type: "UPDATE_QTY", id: item.id, qty: item.qty + 1 }); }}
                      style={{ width: 26, height: 26, borderRadius: "var(--radius)", border: "1px solid var(--border)", color: inventoryManagement && item.qty >= item.stock ? "var(--text-muted)" : "var(--text)", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center", cursor: inventoryManagement && item.qty >= item.stock ? "default" : "pointer" }}
                    >+</button>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 16, flexShrink: 0 }}>
                  <button onClick={() => dispatch({ type: "REMOVE", id: item.id })} style={{ color: "var(--text-muted)", fontSize: "14px", lineHeight: 1, transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "var(--red)"}
                    onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}>✕</button>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "15px", color: "var(--amber)" }}>${(item.price * item.qty).toFixed(2)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {nudgeCount > 0 && (
          <div style={{ margin: "0 24px 6px", background: "rgba(0,212,170,0.06)", border: "1px solid rgba(0,212,170,0.15)", borderRadius: "var(--radius)", padding: "8px 10px", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)", display: "flex", gap: 8, alignItems: "center" }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
            <span><b style={{ color: "var(--amber)" }}>{nudgeCount} more vial{nudgeCount === 1 ? "" : "s"}</b> unlocks 20% off all {eligibleUnits + nudgeCount}</span>
          </div>
        )}

        {/* Coupon input */}
        {cart.length > 0 && (
          <div style={{ padding: "12px 24px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
            {appliedCoupon ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(74,158,106,0.1)", border: "1px solid var(--green)", borderRadius: "var(--radius)", padding: "8px 12px" }}>
                <div>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--green)", fontWeight: 700 }}>{appliedCoupon.code}</span>
                  <span style={{ fontSize: "12px", color: "var(--text-dim)", marginLeft: 8 }}>
                    {appliedCoupon.type === "percent" ? `${appliedCoupon.value}% off` : appliedCoupon.type === "fixed" ? `$${Number(appliedCoupon.value).toFixed(2)} off` : "Free shipping"}
                  </span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setAppliedCoupon(null); if (appliedCoupon?.isAffiliate) { localStorage.removeItem("_ref"); setAffDismissed(true); } }} style={{ fontSize: "12px", color: "var(--text-muted)", background: "none", border: "none", cursor: "pointer", padding: "4px 8px" }}
                  onMouseEnter={e => e.currentTarget.style.color = "var(--red)"}
                  onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}>Remove</button>
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  value={couponInput}
                  onChange={e => { setCouponInput(e.target.value); setCouponError(""); }}
                  onKeyDown={e => e.key === "Enter" && handleApplyCoupon()}
                  placeholder="Coupon code"
                  style={{ flex: 1, fontSize: "13px", padding: "7px 10px" }}
                />
                <Btn small disabled={couponLoading || !couponInput.trim()} onClick={handleApplyCoupon}>
                  {couponLoading ? "…" : "Apply"}
                </Btn>
              </div>
            )}
            {couponError && <p style={{ color: "var(--red)", fontSize: "12px", fontFamily: "var(--font-mono)", marginTop: 6 }}>{couponError}</p>}
          </div>
        )}

        {/* Footer — totals + checkout */}
        {cart.length > 0 && (
          <div style={{ padding: "20px 24px", borderTop: "1px solid var(--border)", flexShrink: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "14px" }}>
              <span style={{ color: "var(--text-dim)" }}>Subtotal</span>
              <span style={{ fontFamily: "var(--font-mono)" }}>${subtotal.toFixed(2)}</span>
            </div>
            {bulkSavings > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, fontSize: 14 }}>
                <span style={{ color: "var(--green)" }}>10-pack savings (20%)<small style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 10.5, opacity: 0.75 }}>{discountedUnits === eligibleUnits ? `${discountedUnits} vials` : `${discountedUnits} of ${eligibleUnits} vials · lowest-priced first`}</small></span>
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${bulkSavings.toFixed(2)}</span>
              </div>
            )}
            {discount > 0 && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "14px" }}>
                  <span style={{ color: "var(--green)" }}>{appliedCoupon?.isAffiliate ? `Referral discount (${appliedCoupon.code})` : `Discount (${appliedCoupon.code})`}</span>
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--green)" }}>−${discount.toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "14px" }}>
                  <span style={{ color: "var(--text-dim)" }}>Discounted subtotal</span>
                  <span style={{ fontFamily: "var(--font-mono)" }}>${(postBulk - discount).toFixed(2)}</span>
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontSize: "14px" }}>
              <span style={{ color: "var(--text-dim)" }}>Shipping</span>
              <span style={{ fontFamily: "var(--font-mono)", color: shipping === 0 ? "var(--green)" : "inherit" }}>
                {shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
              <span style={{ fontWeight: 700, fontSize: "15px" }}>Total</span>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: "22px", color: "var(--amber)" }}>${total.toFixed(2)}</span>
            </div>
            <Btn style={{ width: "100%", justifyContent: "center" }} onClick={handleCheckout}>
              {user ? "Proceed to Checkout →" : "Sign In to Checkout →"}
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartModal;
